import React, { useEffect, useMemo, useState } from 'react';

import MaterialTable from 'material-table';

import {
    getMedicalCouponOrderDetail,
    getMedicalCouponReport
} from '../../../actions/admin/medicalAttribution';
import TopProductsDoughnut from '../Dashboard/TopProducts/TopProductsDoughnut';
import MedicalOrderDetailDialog from './MedicalOrderDetailDialog';
import { adminOrder } from '../../../constants/constants';

import {
    Alert, Backdrop, Box, Card, CardContent, CircularProgress, Container,
    FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Typography
} from '@mui/material';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';

const EMPTY_REPORT = {
    rows: [],
    summary: {
        order_count: 0, total: 0, by_doctor: [],
        by_executive: [], top_products: []
    },
    filters: { doctors: [], executives: [] }
};

const money = value => `$${new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(Number(value || 0))}`;

const MetricCard = ({ label, value, caption }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography color="text.secondary" gutterBottom>{label}</Typography>
            <Typography variant="h4">{value}</Typography>
            {caption && <Typography variant="body2" color="text.secondary">{caption}</Typography>}
        </CardContent>
    </Card>
);

const SummaryMetric = ({ label, value, first = false }) => (
    <Grid
        item
        xs={12}
        sm={4}
        sx={{
            py: { xs: 2, sm: 1 },
            px: 2,
            textAlign: 'center',
            borderTop: { xs: first ? 0 : '1px solid', sm: 0 },
            borderLeft: { xs: 0, sm: first ? 0 : '1px solid' },
            borderColor: 'divider'
        }}
    >
        <Typography variant="body2" color="text.secondary" gutterBottom>
            {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {value}
        </Typography>
    </Grid>
);

const EmptyDataState = ({ children }) => (
    <Box
        role="status"
        sx={{
            minHeight: 112,
            px: 2,
            py: 3,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            textAlign: 'center'
        }}
    >
        <InsertChartOutlinedIcon color="disabled" />
        <Typography variant="body2">{children}</Typography>
    </Box>
);

const MedicalOrders = ({ mineOnly = false }) => {
    const [report, setReport] = useState(EMPTY_REPORT);
    const [filters, setFilters] = useState({
        doctor_id: '',
        executive_id: mineOnly ? 'mine' : ''
    });
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(adminOrder);

    useEffect(() => {
        let active = true;
        setLoading(true);
        getMedicalCouponReport({ ...filters, mine: mineOnly }).then(result => {
            if (!active) return;
            if (result.status) {
                setReport(current => ({
                    ...result.resp,
                    filters: {
                        ...result.resp.filters,
                        executives: !mineOnly && filters.executive_id === 'mine' ?
                            current.filters.executives
                        : result.resp.filters.executives
                    }
                }));
                setFailed(false);
            } else {
                setFailed(true);
            }
            setLoading(false);
        });
        return () => { active = false; };
    }, [filters, mineOnly]);

    const columns = useMemo(() => [
        { title: 'Pedido', field: 'order_num' },
        { title: 'Fecha', field: 'formatted_order_date' },
        { title: 'Cliente', field: 'customer' },
        { title: 'Cupón', field: 'coupon_code' },
        { title: 'Médico', field: 'doctor' },
        { title: 'Ejecutivo', field: 'executive' },
        { title: 'Estatus', field: 'payment_status' },
        { title: 'Total', field: 'total', render: row => money(row.total) }
    ], []);

    const rows = report.rows;
    const hasOrders = Number(report.summary.order_count) > 0;
    const noDoctorsAvailable = !loading && !failed && report.filters.doctors.length === 0;
    const noDoctorsMessage = mineOnly || filters.executive_id === 'mine' ?
        'No tienes médicos disponibles.'
    : filters.executive_id ?
        'Este ejecutivo no tiene médicos disponibles.'
    : 'No hay médicos disponibles.';
    const topProductsChart = useMemo(() => ({
        labels: report.summary.top_products.map(product => product.title),
        datasets: [{
            label: 'Unidades vendidas',
            data: report.summary.top_products.map(product => product.quantity),
            backgroundColor: [
                '#2699FB', '#93D50A', '#FFB547', '#7E57C2', '#26A69A',
                '#EF5350', '#42A5F5', '#D4E157', '#AB47BC', '#78909C'
            ],
            borderWidth: 1
        }]
    }), [report.summary.top_products]);

    const change = ({ target: { name, value } }) => {
        if (name === 'executive_id') {
            setLoading(true);
            setFilters(current => ({
                ...current,
                executive_id: value,
                doctor_id: ''
            }));
            return;
        }
        setFilters(current => ({ ...current, [name]: value }));
    };

    const openOrderDetail = async (_, row) => {
        setDetailLoading(true);
        const result = await getMedicalCouponOrderDetail(
            row._id,
            mineOnly || filters.executive_id === 'mine'
        );
        setDetailLoading(false);
        if (result.status) {
            setSelectedOrder(result.resp);
            setDetailOpen(true);
        } else {
            setFailed(true);
        }
    };

    return (
        <Container maxWidth="xxxl" sx={{ mt: '90px', mb: 5 }}>
            <Typography variant="h4" gutterBottom>
                {mineOnly ? 'Mi resumen médico' : 'Pedidos con atribución médica'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
                {mineOnly ?
                    'Pedidos atribuidos a los médicos que registraste en tu sesión.'
                : 'Pedidos que utilizaron un cupón enlazado a un médico.'}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {!mineOnly && <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" disabled={loading}>
                        <InputLabel>Ejecutivo</InputLabel>
                        <Select label="Ejecutivo" name="executive_id" value={filters.executive_id} onChange={change}>
                            <MenuItem value="">Todos los ejecutivos</MenuItem>
                            <MenuItem value="mine">Mi resumen</MenuItem>
                            {report.filters.executives.map(executive => (
                                <MenuItem key={executive._id} value={executive._id}>{executive.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>}
                <Grid item xs={12} md={mineOnly ? 12 : 6}>
                    <FormControl fullWidth size="small" disabled={loading || noDoctorsAvailable}>
                        <InputLabel>Médico</InputLabel>
                        <Select label="Médico" name="doctor_id" value={filters.doctor_id} onChange={change}>
                            <MenuItem value="">Todos los médicos</MenuItem>
                            {report.filters.doctors.map(doctor => (
                                <MenuItem key={doctor._id} value={doctor._id}>{doctor.name}</MenuItem>
                            ))}
                        </Select>
                        {noDoctorsAvailable && <FormHelperText>{noDoctorsMessage}</FormHelperText>}
                    </FormControl>
                </Grid>
            </Grid>

            {failed && <Alert severity="error" sx={{ mb: 3 }}>No fue posible generar el reporte.</Alert>}
            {loading ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box> : <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {mineOnly ?
                        <Grid item xs={12}>
                            <Card sx={{ boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Resumen de mis pedidos
                                    </Typography>
                                    <Grid container>
                                        <SummaryMetric
                                            first
                                            label="Pedidos"
                                            value={report.summary.order_count}
                                        />
                                        <SummaryMetric
                                            label="Total de pedidos"
                                            value={money(report.summary.total)}
                                        />
                                        <SummaryMetric
                                            label="Médicos"
                                            value={report.summary.by_doctor.length}
                                        />
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    : <>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard label="Pedidos" value={report.summary.order_count} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard label="Total de pedidos" value={money(report.summary.total)} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard label="Médicos" value={report.summary.by_doctor.length} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <MetricCard label="Ejecutivos" value={report.summary.by_executive.length} />
                        </Grid>
                    </>}
                </Grid>

                {!hasOrders && !failed &&
                    <Alert severity="info" sx={{ mb: 3 }}>
                        No se encontraron pedidos con cupón médico para los filtros seleccionados.
                    </Alert>
                }

                <Card sx={{ mb: 4, boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Productos más vendidos con cupón médico
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            El gráfico respeta los filtros de médico y ejecutivo seleccionados.
                        </Typography>
                        {report.summary.top_products.length > 0 ?
                            <TopProductsDoughnut
                                label="Top 10 por unidades vendidas"
                                data={topProductsChart}
                            />
                        :
                            <EmptyDataState>
                                Los pedidos filtrados no contienen productos.
                            </EmptyDataState>
                        }
                    </CardContent>
                </Card>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Pedidos por médico</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {report.summary.by_doctor.length > 0 ?
                        report.summary.by_doctor.map(item => (
                            <Grid item xs={12} sm={6} lg={3} key={item._id}>
                                <MetricCard label={item.name} value={item.order_count} caption={money(item.total)} />
                            </Grid>
                        ))
                    :
                        <Grid item xs={12}>
                            <EmptyDataState>
                                No hay pedidos por médico para los filtros seleccionados.
                            </EmptyDataState>
                        </Grid>
                    }
                </Grid>

                {!mineOnly && <>
                    <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Pedidos por ejecutivo</Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        {report.summary.by_executive.length > 0 ?
                            report.summary.by_executive.map(item => (
                                <Grid item xs={12} sm={6} lg={3} key={item._id}>
                                    <MetricCard label={item.name} value={item.order_count} caption={money(item.total)} />
                                </Grid>
                            ))
                        :
                            <Grid item xs={12}>
                                <EmptyDataState>
                                    No hay pedidos por ejecutivo para los filtros seleccionados.
                                </EmptyDataState>
                            </Grid>
                        }
                    </Grid>
                </>}

                <MaterialTable
                    title="Pedidos atribuidos"
                    data={rows}
                    columns={columns}
                    options={{
                        filtering: true,
                        exportButton: true,
                        pageSize: 10,
                        rowStyle: { cursor: 'pointer' }
                    }}
                    onRowClick={openOrderDetail}
                    localization={{
                        body: { emptyDataSourceMessage: 'No hay pedidos por mostrar' },
                        toolbar: { searchPlaceholder: 'Buscar', exportName: 'Exportar CSV' },
                        pagination: { labelRowsSelect: 'filas', labelRowsPerPage: 'Filas por página:' }
                    }}
                />
            </>}
            <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.modal + 1 }} open={detailLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <MedicalOrderDetailDialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                order={selectedOrder}
                setOrder={setSelectedOrder}
            />
        </Container>
    );
};

export default MedicalOrders;
